import React, { useEffect, useMemo, useState } from "react";
import { Building2, Save, Plus, X, Sparkles } from "lucide-react";
import { Layout } from "@/components/Layout";
import { TourButton } from "@/components/TourButton";
import { useTour } from "@/hooks/useTour";
import { perfilEmpresaTour } from "@/tours";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { getCompanyProfile, putCompanyProfile } from "@/lib/api";

const COMMON_CERTS = [
    "ISO 9001",
    "ISO 14001",
    "ISO 27001",
    "ISO 45001",
    "ISO 50001",
    "EMAS",
];

export default function CompanyProfile() {
    const tour = useTour("tour:perfil-empresa", perfilEmpresaTour, { autoStart: true });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        company_name: "",
        company_cif: "",
        legal_representative: "",
        representative_id: "",
        address: "",
        phone: "",
        email: "",
        website: "",
        certifications: [],
        annual_revenue: "",
        employees: "",
        experience_years: "",
        notes: "",
    });

    useEffect(() => {
        (async () => {
            try {
                const p = await getCompanyProfile();
                setForm({
                    company_name: p.company_name || "",
                    company_cif: p.company_cif || "",
                    legal_representative: p.legal_representative || "",
                    representative_id: p.representative_id || "",
                    address: p.address || "",
                    phone: p.phone || "",
                    email: p.email || "",
                    website: p.website || "",
                    certifications: p.certifications || [],
                    annual_revenue: p.annual_revenue ?? "",
                    employees: p.employees ?? "",
                    experience_years: p.experience_years ?? "",
                    notes: p.notes || "",
                });
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggleCert = (c) =>
        update(
            "certifications",
            form.certifications.includes(c)
                ? form.certifications.filter((x) => x !== c)
                : [...form.certifications, c]
        );
    const customCerts = useMemo(
        () => form.certifications.filter((c) => !COMMON_CERTS.includes(c)),
        [form.certifications]
    );
    const [newCert, setNewCert] = useState("");
    const addCustomCert = () => {
        const c = newCert.trim();
        if (c && !form.certifications.includes(c)) update("certifications", [...form.certifications, c]);
        setNewCert("");
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const body = {
                ...form,
                annual_revenue: form.annual_revenue === "" ? null : Number(form.annual_revenue),
                employees: form.employees === "" ? null : Number(form.employees),
                experience_years: form.experience_years === "" ? null : Number(form.experience_years),
            };
            await putCompanyProfile(body);
            toast.success("Perfil de empresa guardado");
        } catch {
            toast.error("Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto p-6 md:p-10" data-testid="company-profile-page">
                <div className="mb-8">
                    <div className="overline mb-2 flex items-center gap-1.5">
                        <Building2 size={12} /> Perfil de empresa
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl tracking-tight font-medium">
                        Datos y plantilla de empresa
                    </h1>
                    <p className="text-sm md:text-base text-slate-500 mt-2 max-w-2xl">
                        Estos datos se reutilizan automáticamente al generar anexos (DEUC,
                        declaración responsable, solvencia) para ahorrarte trabajo repetitivo.
                    </p>
                </div>

                {loading ? (
                    <div className="space-y-3">
                        <Skeleton className="h-12" />
                        <Skeleton className="h-12" />
                        <Skeleton className="h-64" />
                    </div>
                ) : (
                    <form
                        onSubmit={submit}
                        className="bg-white border border-slate-200 p-6 md:p-8 space-y-8"
                    >
                        <Section title="Identificación">
                            <Grid>
                                <Field label="Razón social">
                                    <Input
                                        value={form.company_name}
                                        onChange={(e) => update("company_name", e.target.value)}
                                        className="rounded-xl border-slate-300"
                                        placeholder="ACME Soluciones, S.L."
                                        data-testid="input-company-name"
                                    />
                                </Field>
                                <Field label="CIF">
                                    <Input
                                        value={form.company_cif}
                                        onChange={(e) => update("company_cif", e.target.value)}
                                        className="rounded-xl border-slate-300 font-mono"
                                        placeholder="B12345678"
                                        data-testid="input-company-cif"
                                    />
                                </Field>
                                <Field label="Representante legal">
                                    <Input
                                        value={form.legal_representative}
                                        onChange={(e) =>
                                            update("legal_representative", e.target.value)
                                        }
                                        className="rounded-xl border-slate-300"
                                        placeholder="María García López"
                                    />
                                </Field>
                                <Field label="DNI/NIE representante">
                                    <Input
                                        value={form.representative_id}
                                        onChange={(e) =>
                                            update("representative_id", e.target.value)
                                        }
                                        className="rounded-xl border-slate-300 font-mono"
                                        placeholder="12345678A"
                                    />
                                </Field>
                            </Grid>
                        </Section>

                        <Section title="Contacto y ubicación">
                            <Grid>
                                <Field label="Dirección">
                                    <Input
                                        value={form.address}
                                        onChange={(e) => update("address", e.target.value)}
                                        className="rounded-xl border-slate-300"
                                        placeholder="Calle Mayor 10, 28013 Madrid"
                                    />
                                </Field>
                                <Field label="Teléfono">
                                    <Input
                                        value={form.phone}
                                        onChange={(e) => update("phone", e.target.value)}
                                        className="rounded-xl border-slate-300"
                                        placeholder="+34 912 345 678"
                                    />
                                </Field>
                                <Field label="Email">
                                    <Input
                                        value={form.email}
                                        onChange={(e) => update("email", e.target.value)}
                                        className="rounded-xl border-slate-300"
                                        placeholder="contacto@empresa.com"
                                    />
                                </Field>
                                <Field label="Web">
                                    <Input
                                        value={form.website}
                                        onChange={(e) => update("website", e.target.value)}
                                        className="rounded-xl border-slate-300"
                                        placeholder="https://empresa.com"
                                    />
                                </Field>
                            </Grid>
                        </Section>

                        <Section title="Datos económicos">
                            <Grid cols={3}>
                                <Field label="Facturación anual (€)">
                                    <Input
                                        type="number"
                                        step="1000"
                                        value={form.annual_revenue}
                                        onChange={(e) => update("annual_revenue", e.target.value)}
                                        className="rounded-xl border-slate-300 font-mono"
                                        placeholder="1200000"
                                        data-testid="input-annual-revenue"
                                    />
                                </Field>
                                <Field label="Empleados">
                                    <Input
                                        type="number"
                                        value={form.employees}
                                        onChange={(e) => update("employees", e.target.value)}
                                        className="rounded-xl border-slate-300 font-mono"
                                        placeholder="25"
                                    />
                                </Field>
                                <Field label="Años de experiencia">
                                    <Input
                                        type="number"
                                        value={form.experience_years}
                                        onChange={(e) => update("experience_years", e.target.value)}
                                        className="rounded-xl border-slate-300 font-mono"
                                        placeholder="12"
                                    />
                                </Field>
                            </Grid>
                        </Section>

                        <Section title="Certificaciones">
                            <div className="flex flex-wrap gap-2 mb-3">
                                {COMMON_CERTS.map((c) => {
                                    const active = form.certifications.includes(c);
                                    return (
                                        <button
                                            key={c}
                                            type="button"
                                            onClick={() => toggleCert(c)}
                                            className={`text-xs font-medium px-3 h-8 rounded-xl border transition-colors ${
                                                active
                                                    ? "bg-[#0055ff] text-white border-[#0055ff]"
                                                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                                            }`}
                                            data-testid={`cert-toggle-${c}`}
                                        >
                                            {c}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex flex-wrap gap-2 mb-3">
                                {customCerts.map((c) => (
                                    <span
                                        key={c}
                                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 h-8 rounded-xl border border-[#0055ff] bg-[#0055ff] text-white"
                                    >
                                        {c}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                update(
                                                    "certifications",
                                                    form.certifications.filter((x) => x !== c)
                                                )
                                            }
                                            className="hover:opacity-80"
                                        >
                                            <X size={12} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <Input
                                    value={newCert}
                                    onChange={(e) => setNewCert(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            addCustomCert();
                                        }
                                    }}
                                    placeholder="Añadir otra certificación…"
                                    className="rounded-xl border-slate-300 max-w-sm"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={addCustomCert}
                                    className="rounded-xl border-slate-300"
                                >
                                    <Plus size={14} className="mr-1" />
                                    Añadir
                                </Button>
                            </div>
                        </Section>

                        <Section title="Notas internas">
                            <Textarea
                                value={form.notes}
                                onChange={(e) => update("notes", e.target.value)}
                                rows={4}
                                className="rounded-xl border-slate-300"
                                placeholder="Información adicional sobre la empresa (equipo directivo, principales clientes, etc.)"
                            />
                        </Section>

                        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                <Sparkles size={12} className="text-[#0055ff]" />
                                Los anexos generados usarán estos datos por defecto.
                            </div>
                            <Button
                                type="submit"
                                disabled={saving}
                                className="rounded-xl bg-[#0055ff] hover:bg-[#0044cc] text-white"
                                data-testid="save-profile-btn"
                            >
                                {saving ? (
                                    "Guardando…"
                                ) : (
                                    <>
                                        <Save size={14} className="mr-1.5" />
                                        Guardar perfil
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                )}
            </div>
            <TourButton onClick={tour.start} label="Ver tour" />
        </Layout>
    );
}

const Section = ({ title, children }) => (
    <div>
        <div className="overline mb-4">{title}</div>
        {children}
    </div>
);
const Grid = ({ cols = 2, children }) => (
    <div className={`grid md:grid-cols-${cols} gap-5`}>{children}</div>
);
const Field = ({ label, children }) => (
    <div>
        <Label className="overline block mb-1.5 !text-slate-400">{label}</Label>
        {children}
    </div>
);
